import uniq from 'lodash/uniq';
import {
  existingDestinationsForExistingSource,
  existingSourcesForExistingDestination,
  newSourcesForExistingDestination,
} from '@components/workflows/utils';
import useStores from '@stores/useStores';
import { areAllSourcesInDeviceMode } from '@components/destinations/destination-view/utils';
import { getMessageList } from '@lib/utils/getMessageList';
import { ActivationSourceType } from '@stores/retlSources/types';
import sourceFactory from '@components/sources/sourceFactory';

import { useActionGuard, ActionType, ResourceType } from '..';
import { QueryResult } from '../types';
import {
  constructPermissionResult,
  getResourceTypeFromSourceCategory,
  getResourceTypeFromSourceDefinitionCategory,
} from '.';

const SOURCE_NOT_FOUND = 'Source not found';
const DESTINATION_NOT_FOUND = 'Destination not found';

const findProfilesProjectIdBySourceId = (sourceId: string) => {
  const { retlSourceListStore } = useStores();

  const retlSource = retlSourceListStore.getRetlSourceById<ActivationSourceType>(sourceId);

  return retlSource?.config?.whtProjectId;
};

export const canConnectSourceToExistingDestination = (sourceId: string): QueryResult => {
  const {
    permissionsStore: { hasPermission },
    sourcesListStore,
    destinationsListStore,
  } = useStores();

  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const source = sourcesListStore.sourceById(sourceId);

  if (!source) {
    return { isAllowed: false, denialReason: SOURCE_NOT_FOUND };
  }

  const compatibleDestinations = existingDestinationsForExistingSource(
    destinationsListStore.destinations,
    source,
  ).map(({ id }) => id);

  if (compatibleDestinations.length === 0) {
    return {
      isAllowed: false,
      denialReason: 'There are no compatible destinations to connect to this source.',
    };
  }

  const profilesProjectId = findProfilesProjectIdBySourceId(sourceId);

  const permission = isNewPermissionModelEnforcementEnabled
    ? canPerformAction({
        new: {
          actionType: ActionType.CONNECT_EXISTING,
          resources: [
            profilesProjectId
              ? { type: ResourceType.PROFILES_PROJECT, id: profilesProjectId }
              : { type: getResourceTypeFromSourceCategory(source.sourceCategory), id: sourceId },
            { type: ResourceType.DESTINATION, id: compatibleDestinations },
          ],
        },
      }).isAllowed
    : hasPermission({ resourceType: 'source', action: 'edit', resourceId: sourceId }) &&
      compatibleDestinations.some((id) =>
        hasPermission({ resourceType: 'destination', action: 'edit', resourceId: id }),
      );

  return constructPermissionResult(
    permission,
    'You do not have permission to connect existing destinations to this source.',
  );
};

export const canConnectSourceToNewDestination = (sourceId: string): QueryResult => {
  const {
    permissionsStore: { hasPermission },
    sourcesListStore,
  } = useStores();

  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const source = sourcesListStore.sourceById(sourceId);

  if (!source) {
    return { isAllowed: false, denialReason: SOURCE_NOT_FOUND };
  }

  const profilesProjectId = findProfilesProjectIdBySourceId(sourceId);

  const permission = isNewPermissionModelEnforcementEnabled
    ? canPerformAction({
        new: {
          actionType: ActionType.CONNECT_NEW,
          resources: [
            profilesProjectId
              ? { type: ResourceType.PROFILES_PROJECT, id: profilesProjectId }
              : { type: getResourceTypeFromSourceCategory(source.sourceCategory), id: sourceId },
            { type: ResourceType.DESTINATION },
          ],
        },
      }).isAllowed
    : hasPermission({ resourceType: 'source', action: 'edit', resourceId: sourceId }) &&
      hasPermission({ resourceType: 'destination', action: 'create' });

  return constructPermissionResult(
    permission,
    'You do not have permission to create and thus to connect new destinations to this source.',
  );
};

export const canConnectSourceToDestination = (sourceId: string): QueryResult => {
  const connectSourceToExistingDestinationPermission =
    canConnectSourceToExistingDestination(sourceId);
  const connectSourceToNewDestinationPermission = canConnectSourceToNewDestination(sourceId);

  return constructPermissionResult(
    connectSourceToExistingDestinationPermission.isAllowed ||
      connectSourceToNewDestinationPermission.isAllowed,
    'You do not have permission to connect destinations to this source.',
  );
};

export const canConnectDestinationToExistingSource = (destinationId: string): QueryResult => {
  const {
    permissionsStore: { hasPermission },
    sourcesListStore,
    destinationsListStore,
  } = useStores();

  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const destination = destinationsListStore.destinationById(destinationId);

  if (!destination) {
    return { isAllowed: false, denialReason: DESTINATION_NOT_FOUND };
  }

  const compatibleSources = existingSourcesForExistingDestination(
    sourcesListStore.sources,
    destination,
  ).map(({ id }) => id);

  if (compatibleSources.length === 0) {
    return {
      isAllowed: false,
      denialReason: 'There are no compatible sources to connect to this destination.',
    };
  }

  return isNewPermissionModelEnforcementEnabled
    ? canPerformAction({
        new: {
          actionType: ActionType.CONNECT_EXISTING,
          resources: [
            { type: ResourceType.DESTINATION, id: destinationId },
            { type: ResourceType.SOURCE, id: compatibleSources },
          ],
        },
      })
    : constructPermissionResult(
        hasPermission({
          resourceType: 'destination',
          action: 'edit',
          resourceId: destinationId,
        }) &&
          compatibleSources.some((id) =>
            hasPermission({ resourceType: 'source', action: 'edit', resourceId: id }),
          ),
        'You do not have permission to connect existing sources to this destination.',
      );
};

export const canConnectDestinationToNewSource = (destinationId: string): QueryResult => {
  const {
    permissionsStore: { hasPermission },
    sourceDefinitionsListStore,
    destinationDefsListStore,
    destinationsListStore,
  } = useStores();

  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const destination = destinationsListStore.destinationById(destinationId);

  if (!destination) {
    return { isAllowed: false, denialReason: DESTINATION_NOT_FOUND };
  }

  const compatibleSourceDefinitions = newSourcesForExistingDestination(
    sourceDefinitionsListStore.sourceDefinitions,
    destinationDefsListStore.getDestinationDef(destination.destinationDefinition.id)!,
  );

  const compatibleSourceResourceTypes = uniq(
    compatibleSourceDefinitions.map((s) => getResourceTypeFromSourceDefinitionCategory(s.category)),
  );

  return isNewPermissionModelEnforcementEnabled
    ? canPerformAction({
        new: {
          actionType: ActionType.CONNECT_NEW,
          resources: [
            { type: ResourceType.DESTINATION, id: destinationId },
            { type: compatibleSourceResourceTypes },
          ],
        },
      })
    : constructPermissionResult(
        hasPermission({
          resourceType: 'destination',
          action: 'edit',
          resourceId: destinationId,
        }) && hasPermission({ resourceType: 'source', action: 'create' }),
        'You do not have permission to create and thus to connect new sources to this destination.',
      );
};

export const canConnectOrDisconnectSourceAndDestination = ({
  sourceId,
  destinationId,
  actionType,
}: {
  sourceId: string;
  destinationId: string;
  actionType: typeof ActionType.CONNECT | typeof ActionType.DISCONNECT;
}): QueryResult => {
  const {
    permissionsStore: { hasPermission },
    sourcesListStore,
    destinationsListStore,
  } = useStores();

  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const source = sourcesListStore.sourceById(sourceId);
  const destination = destinationsListStore.destinationById(destinationId);

  if (!source || !destination) {
    return { isAllowed: false, denialReason: 'Source or destination not found' };
  }

  const profilesProjectId = findProfilesProjectIdBySourceId(sourceId);

  const permission = isNewPermissionModelEnforcementEnabled
    ? canPerformAction({
        new: {
          actionType,
          resources: [
            profilesProjectId
              ? { type: ResourceType.PROFILES_PROJECT, id: profilesProjectId }
              : { type: getResourceTypeFromSourceCategory(source.sourceCategory), id: source.id },
            { type: ResourceType.DESTINATION, id: destinationId },
          ],
        },
      }).isAllowed
    : hasPermission({ resourceType: 'source', action: 'edit', resourceId: source.id }) &&
      hasPermission({ resourceType: 'destination', action: 'edit', resourceId: destinationId });

  return constructPermissionResult(
    permission,
    `You do not have permission to ${actionType} this source and this destination.`,
  );
};

export const canViewSourceLiveEvents = (sourceId: string): QueryResult => {
  const { sourcesListStore } = useStores();
  const { canPerformAction } = useActionGuard();

  const source = sourcesListStore.sourceById(sourceId);

  if (!source) {
    return { isAllowed: false, denialReason: SOURCE_NOT_FOUND };
  }

  if (!source.enabled) {
    return { isAllowed: false, denialReason: 'This source is disabled.' };
  }

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'pii' },
    new: {
      actionType: ActionType.PII_LIVE_EVENTS,
      resource: {
        type: getResourceTypeFromSourceCategory(source.sourceCategory),
        id: source.id,
      },
    },
  });
};

export const canViewDestinationLiveEvents = (destinationId: string): QueryResult => {
  const { destinationsListStore } = useStores();
  const { canPerformAction } = useActionGuard();

  const destination = destinationsListStore.destinationById(destinationId);

  if (!destination) {
    return { isAllowed: false, denialReason: DESTINATION_NOT_FOUND };
  }

  if (destination.destinationDefinition.options?.disableLiveEvents) {
    return {
      isAllowed: false,
      denialReason: 'Live events view is not available for this destination.',
    };
  }

  if (!destination.enabled) {
    return { isAllowed: false, denialReason: 'This destination is disabled.' };
  }

  if (destination.sources.length === 0) {
    return {
      isAllowed: false,
      denialReason: 'There are no sources connected to this destination.',
    };
  }

  const isDeviceMode = areAllSourcesInDeviceMode(destination.config, destination.sources);

  if (isDeviceMode) {
    return {
      isAllowed: false,
      denialReason: 'This destination is being used only in device mode.',
    };
  }

  const areAllSourcesDisabled = destination.sources
    .map((s) => s.enabled)
    .every((isEnabled) => !isEnabled);

  if (areAllSourcesDisabled) {
    return {
      isAllowed: false,
      denialReason: 'All sources connected to this destination are disabled.',
    };
  }

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'pii' },
    new: {
      actionType: ActionType.PII_LIVE_EVENTS,
      resource: { type: ResourceType.DESTINATION, id: destination.id },
    },
  });
};

export const canCreateDestination = () => {
  const { canPerformAction } = useActionGuard();
  return canPerformAction({
    old: { resourceType: 'destination', action: 'create' },
    new: {
      actionType: ActionType.CREATE,
      resource: { type: ResourceType.DESTINATION },
    },
  });
};

export const canEditDestination = (destinationId: string): QueryResult => {
  const { canPerformAction } = useActionGuard();
  return canPerformAction({
    old: { resourceType: 'destination', action: 'edit', resourceId: destinationId },
    new: {
      actionType: ActionType.EDIT,
      resource: { type: ResourceType.DESTINATION, id: destinationId },
    },
  });
};

export const canDeleteDestination = (destinationId: string): QueryResult => {
  const { destinationsListStore } = useStores();

  const { canPerformAction } = useActionGuard();

  const destination = destinationsListStore.destinationById(destinationId);

  if (!destination) {
    return { isAllowed: false, denialReason: DESTINATION_NOT_FOUND };
  }

  if (destination.sources.length > 0) {
    return {
      isAllowed: false,
      denialReason: 'You cannot delete a destination that has connected sources.',
    };
  }

  return canPerformAction({
    old: { resourceId: destinationId, resourceType: 'destination', action: 'delete' },
    new: {
      actionType: ActionType.DELETE,
      resource: { id: destinationId, type: ResourceType.DESTINATION },
    },
  });
};

export const canEditSource = (sourceId: string): QueryResult => {
  const { canPerformAction } = useActionGuard();
  const { sourcesListStore } = useStores();
  const source = sourcesListStore.sourceById(sourceId);

  if (!source) {
    return { isAllowed: false, denialReason: SOURCE_NOT_FOUND };
  }

  const profilesProjectId = findProfilesProjectIdBySourceId(sourceId);

  return constructPermissionResult(
    canPerformAction({
      old: { resourceType: 'source', action: 'edit', resourceId: sourceId },
      new: {
        actionType: ActionType.EDIT,
        resource: profilesProjectId
          ? { type: ResourceType.PROFILES_PROJECT, id: profilesProjectId }
          : {
              type: getResourceTypeFromSourceCategory(source.sourceCategory),
              id: sourceId,
            },
      },
    }).isAllowed,
    'You do not have permission to edit this source.',
  );
};

export const canDeleteSource = (sourceId: string): QueryResult => {
  const { canPerformAction } = useActionGuard();
  const { sourcesListStore } = useStores();
  const source = sourcesListStore.sourceById(sourceId);

  if (!source) {
    return { isAllowed: false, denialReason: SOURCE_NOT_FOUND };
  }

  if (source.destinations.length > 0) {
    return {
      isAllowed: false,
      denialReason: 'You cannot delete a source that has connected destinations.',
    };
  }

  const profilesProjectId = findProfilesProjectIdBySourceId(sourceId);

  return constructPermissionResult(
    canPerformAction({
      old: { resourceType: 'source', action: 'delete', resourceId: sourceId },
      new: profilesProjectId
        ? {
            actionType: ActionType.EDIT,
            resource: { type: ResourceType.PROFILES_PROJECT, id: profilesProjectId },
          }
        : {
            actionType: ActionType.DELETE,
            resource: {
              type: getResourceTypeFromSourceCategory(source.sourceCategory),
              id: sourceId,
            },
          },
    }).isAllowed,
    'You do not have permission to delete this source.',
  );
};

export const canEnableOrDisableSource = (sourceId: string) => {
  const { sourcesListStore, featuresStore } = useStores();
  const source = sourcesListStore.sourceById(sourceId);

  if (!source) {
    return { isAllowed: false, denialReason: SOURCE_NOT_FOUND };
  }

  const sunsetReason = sourceFactory(source.sourceDef.category).getSunsetReason(
    source.sourceDef.name,
    featuresStore,
  );

  if (sunsetReason) {
    return { isAllowed: false, denialReason: sunsetReason };
  }

  return canEditSource(sourceId);
};

export const canPerformWarehouseSyncs = (
  type: 'sync' | 'retry',
  destinationId?: string,
): QueryResult => {
  const { canPerformAction } = useActionGuard();

  const permission = canPerformAction({
    new: {
      actionType: ActionType.EDIT,
      resource: { type: ResourceType.RETRY_SYNC },
    },
    old: destinationId
      ? { resourceType: 'destination', action: 'edit', resourceId: destinationId }
      : undefined,
  });

  return constructPermissionResult(
    permission.isAllowed,
    `You do not have permission to ${type === 'sync' ? 'perform' : 'retry'} warehouse syncs.`,
  );
};

export const canViewAuditLogs = (): boolean => {
  const { userStore, permissionsStore, featuresStore } = useStores();
  const { isNewPermissionModelEnforcementEnabled } = useActionGuard();

  return (
    featuresStore.has('AUDIT_LOGS') &&
    (isNewPermissionModelEnforcementEnabled
      ? userStore.isOrgAdmin
      : permissionsStore.hasPermission({ resourceType: 'workspace', action: 'edit' }))
  );
};

const canCreateOrDeleteDestinationAccount = (action: 'create' | 'delete'): QueryResult => {
  const { canPerformAction } = useActionGuard();

  return constructPermissionResult(
    canPerformAction({
      new: { actionType: ActionType.CREATE, resource: { type: ResourceType.DESTINATION } },
      old: { resourceType: 'workspace', action: 'edit' },
    }).isAllowed,
    `You do not have permission to ${action} destination accounts.`,
  );
};

export const canCreateDestinationAccount = () => canCreateOrDeleteDestinationAccount('create');

export const canDeleteDestinationAccount = (accountId?: string) => {
  const { destinationsListStore } = useStores();

  const hasLinkedDestinations =
    accountId &&
    destinationsListStore.destinations.some(
      (destination) => destination.config.rudderAccountId === accountId,
    );

  const deleteDestinationAccountPermission = canCreateOrDeleteDestinationAccount('delete');

  return {
    isAllowed: deleteDestinationAccountPermission.isAllowed && !hasLinkedDestinations,
    denialReason: getMessageList([
      deleteDestinationAccountPermission.denialReason,
      hasLinkedDestinations ? 'You cannot delete an account with linked destinations.' : '',
    ]),
  };
};

const canCreateOrDeleteExtractSourceAccount = (action: 'create' | 'delete'): QueryResult => {
  const { userStore, permissionsStore } = useStores();
  const { isNewPermissionModelEnforcementEnabled } = useActionGuard();

  return constructPermissionResult(
    isNewPermissionModelEnforcementEnabled
      ? userStore.isOrgAdmin
      : permissionsStore.hasPermission({ resourceType: 'workspace', action: 'edit' }),
    `You do not have permission to ${action} extract source accounts.`,
  );
};

export const canCreateExtractSourceAccount = () => canCreateOrDeleteExtractSourceAccount('create');
export const canDeleteExtractSourceAccount = () => canCreateOrDeleteExtractSourceAccount('delete');
